import React from 'react';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Box,
  ButtonBase,
  Collapse,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  collapsedDrawerWidth,
  drawerWidth,
  getVisibleShellNavSections,
  isNavItemActive,
} from './adminNavigation';
import StaffAvatar from './StaffAvatar';

function getSectionPanelId(title) {
  return `nav-section-${title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;
}

function DrawerContent({
  pathname,
  onNavigate,
  collapsed,
  onToggleCollapse,
  profile,
  showCollapseControl,
  showDataStewardshipNavItem = true,
  showAlumCommandCenterNavItem = false,
  shellRole,
  sectionLabel = 'Admin',
  productName = 'OutreachOS',
  collapsedBadgeLabel = 'QR',
  profileHomePath = '/me',
  profileHomeAriaLabel = 'Go to My Dashboard',
}) {
  const navSections = getVisibleShellNavSections(shellRole, {
    showDataStewardship: showDataStewardshipNavItem,
    showAlumCommandCenter: showAlumCommandCenterNavItem,
  });
  const displayName = profile?.full_name || profile?.email || 'Staff user';
  const activeSectionTitle = navSections.find((section) =>
    section.items.some((item) => !item.disabled && isNavItemActive(pathname, item))
  )?.title;
  const [openSectionOverrides, setOpenSectionOverrides] = React.useState({});

  const toggleSection = (title) => {
    setOpenSectionOverrides((current) => ({
      ...current,
      [title]: !current[title],
    }));
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100%', flexDirection: 'column' }}>
      <Toolbar
        sx={{
          px: collapsed ? 1.25 : 2.5,
          alignItems: 'center',
          justifyContent: showCollapseControl ? 'space-between' : 'flex-start',
          gap: 1,
          pt: 1,
        }}
      >
        {collapsed ? (
          <Typography
            variant="overline"
            sx={{
              width: 40,
              height: 40,
              display: 'grid',
              placeItems: 'center',
              borderRadius: 2,
              letterSpacing: '0.12em',
              color: (theme) => theme.custom.drawerActiveText,
              backgroundColor: (theme) => theme.custom.drawerActive,
              border: '1px solid',
              borderColor: (theme) => theme.custom.drawerActiveBorder,
            }}
          >
            {collapsedBadgeLabel}
          </Typography>
        ) : (
          <Box>
            <Typography
              variant="overline"
              sx={{
                letterSpacing: '0.12em',
                color: (theme) => theme.custom.drawerSectionLabel,
              }}
            >
              {sectionLabel}
            </Typography>
            {/*
              Product identity, not a document section: same `h6` visual scale,
              rendered as a plain element so the drawer contributes no heading
              to the routed page's outline.
            */}
            <Typography
              variant="h6"
              component="div"
              sx={{ lineHeight: 1.1, color: 'text.primary' }}
            >
              {productName}
            </Typography>
          </Box>
        )}
        {showCollapseControl ? (
          <Tooltip title={collapsed ? 'Expand navigation' : 'Collapse navigation'}>
            <IconButton
              aria-label={collapsed ? 'Expand navigation' : 'Collapse navigation'}
              onClick={onToggleCollapse}
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                color: 'text.primary',
                backgroundColor: (theme) => theme.custom.topBarButton,
                boxShadow: 'none',
                '&:hover': {
                  backgroundColor: (theme) => theme.custom.drawerHover,
                },
              }}
            >
              {collapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
            </IconButton>
          </Tooltip>
        ) : null}
      </Toolbar>

      <Divider />

      <Box sx={{ flex: 1, overflowY: 'auto', px: collapsed ? 1 : 1.5, py: 2 }}>
        {navSections.map((section, index) => (
          <Box key={section.title} sx={{ mb: index === navSections.length - 1 ? 0 : 2 }}>
            {(() => {
              const sectionActive = activeSectionTitle === section.title;
              const sectionOpen = collapsed || sectionActive || openSectionOverrides[section.title] === true;
              const sectionPanelId = getSectionPanelId(section.title);

              return (
                <>
                  {!collapsed ? (
                    <ListItemButton
                      aria-expanded={sectionOpen}
                      aria-controls={sectionPanelId}
                      onClick={() => toggleSection(section.title)}
                      sx={{
                        mb: 0.5,
                        borderRadius: 1.5,
                        border: '1px solid transparent',
                        px: 1.5,
                        py: 0.5,
                        minHeight: 34,
                        color: (theme) => theme.custom.drawerSectionLabel,
                        '&:hover': {
                          backgroundColor: (theme) => theme.custom.drawerHover,
                          color: 'text.primary',
                        },
                      }}
                    >
                      <ListItemText
                        primary={section.title}
                        primaryTypographyProps={{
                          variant: 'overline',
                          sx: {
                            color: 'inherit',
                            letterSpacing: '0.08em',
                            lineHeight: 1.4,
                          },
                        }}
                      />
                      {sectionOpen ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                    </ListItemButton>
                  ) : null}
                  <Collapse in={sectionOpen} timeout="auto" unmountOnExit>
                    <List id={sectionPanelId} disablePadding sx={{ mt: collapsed ? 0 : 0.75 }}>
                      {section.items.map((item) => {
                        const active = !item.disabled && isNavItemActive(pathname, item);
                        const Icon = item.icon;
                        const tooltipTitle = collapsed
                          ? item.disabledReason
                            ? `${item.label} (${item.disabledReason})`
                            : item.label
                          : '';

                        return (
                          <Tooltip key={item.path} title={tooltipTitle} placement="right">
                            <ListItemButton
                              disabled={item.disabled}
                              selected={active}
                              onClick={item.disabled ? undefined : () => onNavigate(item.path)}
                              sx={{
                                mb: 0.5,
                                borderRadius: 2,
                                border: '1px solid transparent',
                                px: collapsed ? 1.25 : 1.5,
                                justifyContent: collapsed ? 'center' : 'flex-start',
                                color: active ? 'text.primary' : 'text.secondary',
                                '&:hover': {
                                  backgroundColor: (theme) => theme.custom.drawerHover,
                                  color: 'text.primary',
                                },
                                '&.Mui-selected': {
                                  backgroundColor: (theme) => theme.custom.drawerActive,
                                  borderColor: (theme) => theme.custom.drawerActiveBorder,
                                  boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.03)',
                                  '& .MuiListItemIcon-root, & .MuiListItemText-primary': {
                                    color: (theme) => theme.custom.drawerActiveText,
                                  },
                                },
                              }}
                            >
                              <ListItemIcon
                                sx={{
                                  minWidth: collapsed ? 0 : 40,
                                  justifyContent: 'center',
                                  color: active ? (theme) => theme.custom.drawerActiveText : 'inherit',
                                }}
                              >
                                <Icon fontSize="small" />
                              </ListItemIcon>
                              {!collapsed ? (
                                <ListItemText
                                  primary={item.label}
                                  secondary={item.disabledReason || null}
                                />
                              ) : null}
                            </ListItemButton>
                          </Tooltip>
                        );
                      })}
                    </List>
                  </Collapse>
                </>
              );
            })()}
          </Box>
        ))}
      </Box>

      {profile ? (
        <>
          <Divider />
          <Box sx={{ p: collapsed ? 1 : 1.5 }}>
            <Tooltip title={collapsed ? `${displayName} • ${profile.role}` : ''} placement="right">
              <ButtonBase
                aria-label={profileHomeAriaLabel}
                onClick={() => onNavigate(profileHomePath)}
                sx={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: collapsed ? 'center' : 'flex-start',
                  gap: 1.25,
                  px: collapsed ? 0.75 : 1.25,
                  py: 1,
                  borderRadius: 2.5,
                  border: '1px solid',
                  borderColor: 'divider',
                  backgroundColor: (theme) => theme.custom.topBarButton,
                  '&:hover': {
                    backgroundColor: (theme) => theme.custom.drawerHover,
                  },
                }}
              >
                <StaffAvatar
                  avatarUrl={profile.avatar_url}
                  fullName={displayName}
                  size={collapsed ? 32 : 36}
                />
                {!collapsed ? (
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: 700, lineHeight: 1.2 }} noWrap>
                      {displayName}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" noWrap>
                      {profile.role}
                    </Typography>
                  </Box>
                ) : null}
              </ButtonBase>
            </Tooltip>
          </Box>
        </>
      ) : null}
    </Box>
  );
}

const AdminNavDrawer = ({
  mobileOpen,
  onClose,
  pathname,
  onNavigate,
  collapsed,
  onToggleCollapse,
  profile,
  showDataStewardshipNavItem = true,
  showAlumCommandCenterNavItem = false,
  shellRole,
  sectionLabel,
  productName,
  collapsedBadgeLabel,
  profileHomePath,
  profileHomeAriaLabel,
  topBarHeight = 64,
}) => (
  <Box
    component="nav"
    sx={{
      width: { lg: collapsed ? collapsedDrawerWidth : drawerWidth },
      flexShrink: { lg: 0 },
    }}
  >
    <Drawer
      variant="temporary"
      open={mobileOpen}
      onClose={onClose}
      ModalProps={{ keepMounted: true }}
      sx={{
        display: { xs: 'block', lg: 'none' },
        '& .MuiDrawer-paper': {
          boxSizing: 'border-box',
          width: drawerWidth,
          borderRightColor: 'divider',
          boxShadow: 'none',
        },
      }}
    >
      <DrawerContent
        pathname={pathname}
        onNavigate={onNavigate}
        collapsed={false}
        onToggleCollapse={onToggleCollapse}
        profile={profile}
        showCollapseControl={false}
        showDataStewardshipNavItem={showDataStewardshipNavItem}
        showAlumCommandCenterNavItem={showAlumCommandCenterNavItem}
        shellRole={shellRole}
        sectionLabel={sectionLabel}
        productName={productName}
        collapsedBadgeLabel={collapsedBadgeLabel}
        profileHomePath={profileHomePath}
        profileHomeAriaLabel={profileHomeAriaLabel}
      />
    </Drawer>

    <Drawer
      variant="permanent"
      open
      sx={{
        display: { xs: 'none', lg: 'block' },
        '& .MuiDrawer-paper': {
          boxSizing: 'border-box',
          width: collapsed ? collapsedDrawerWidth : drawerWidth,
          top: `${topBarHeight}px`,
          height: `calc(100% - ${topBarHeight}px)`,
          borderRightColor: 'divider',
          overflowX: 'hidden',
          boxShadow: 'none',
        },
      }}
    >
      <DrawerContent
        pathname={pathname}
        onNavigate={onNavigate}
        collapsed={collapsed}
        onToggleCollapse={onToggleCollapse}
        profile={profile}
        showCollapseControl
        showDataStewardshipNavItem={showDataStewardshipNavItem}
        showAlumCommandCenterNavItem={showAlumCommandCenterNavItem}
        shellRole={shellRole}
        sectionLabel={sectionLabel}
        productName={productName}
        collapsedBadgeLabel={collapsedBadgeLabel}
        profileHomePath={profileHomePath}
        profileHomeAriaLabel={profileHomeAriaLabel}
      />
    </Drawer>
  </Box>
);

export default AdminNavDrawer;
